import React from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { formatRupiah } from '../utils/helpers';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Package, 
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Banknote
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const StatCard = ({ title, value, icon: Icon, trend, trendUp, color = "default" }) => {
  const colorClasses = {
    default: "text-foreground",
    success: "text-emerald-500",
    danger: "text-rose-500",
    info: "text-blue-500",
    warning: "text-amber-500"
  };

  return (
    <Card className="bg-white border border-border/60 shadow-none hover:border-border transition-colors duration-200 card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </p>
            <p className={`text-2xl md:text-3xl font-black tracking-tight ${colorClasses[color]}`}>
              {value}
            </p>
            {trend && (
              <div className="flex items-center gap-1 text-xs">
                {trendUp ? (
                  <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-rose-500" />
                )}
                <span className={trendUp ? "text-emerald-500" : "text-rose-500"}>
                  {trend}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-sm ${color === 'success' ? 'bg-emerald-50' : color === 'danger' ? 'bg-rose-50' : color === 'info' ? 'bg-blue-50' : 'bg-zinc-100'}`}>
            <Icon className={`h-5 w-5 ${colorClasses[color]}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border p-3 rounded-sm shadow-lg">
        <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {formatRupiah(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { summary, chartData, pemasukan, pengeluaran, loading } = useApp();

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Memuat data...</div>
      </div>
    );
  }

  const recentPemasukan = [...pemasukan].slice(-5).reverse();
  const recentPengeluaran = [...pengeluaran].slice(-5).reverse();

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          title="Total Pemasukan"
          value={formatRupiah(summary.total_pemasukan)}
          icon={TrendingUp}
          color="success"
        />
        <StatCard
          title="Total Pengeluaran"
          value={formatRupiah(summary.total_pengeluaran)}
          icon={TrendingDown}
          color="danger"
        />
        <StatCard
          title="Saldo"
          value={formatRupiah(summary.saldo)}
          icon={Wallet}
          color={summary.saldo >= 0 ? "success" : "danger"}
        />
        <StatCard
          title="Nilai Inventori"
          value={formatRupiah(summary.nilai_inventori)}
          icon={Package}
          color="info"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-white border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Banknote className="h-4 w-4 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground">Total Modal</p>
                <p className="text-sm font-bold">{formatRupiah(summary.total_modal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <div>
                <p className="text-xs text-muted-foreground">Pendapatan Bersih</p>
                <p className="text-sm font-bold">{formatRupiah(summary.total_pendapatan)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground">Total Item</p>
                <p className="text-sm font-bold">{summary.total_item_inventori}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-4 w-4 text-violet-500" />
              <div>
                <p className="text-xs text-muted-foreground">Jumlah Cabang</p>
                <p className="text-sm font-bold">{summary.jumlah_cabang}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Area Chart */}
        <Card className="bg-white border border-border/60 col-span-1 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold">Tren Keuangan Bulanan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorPemasukan" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorPengeluaran" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#F43F5E" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="bulan" tick={{ fontSize: 12 }} stroke="#71717A" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#71717A" tickFormatter={(v) => `${(v/1000000).toFixed(0)}jt`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="pemasukan"
                      stroke="#10B981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorPemasukan)"
                      name="Pemasukan"
                    />
                    <Area
                      type="monotone"
                      dataKey="pengeluaran"
                      stroke="#F43F5E"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorPengeluaran)"
                      name="Pengeluaran"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Belum ada data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Income */}
        <Card className="bg-white border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Pemasukan Terbaru
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPemasukan.length > 0 ? recentPemasukan.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{item.sumber_dana}</p>
                    <p className="text-xs text-muted-foreground">{item.tanggal}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-bold text-emerald-600">
                      +{formatRupiah(item.jumlah)}
                    </p>
                    {item.is_modal && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                        Modal
                      </span>
                    )}
                  </div>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-4">Belum ada data</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Expenses */}
        <Card className="bg-white border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-rose-500" />
              Pengeluaran Terbaru
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPengeluaran.length > 0 ? recentPengeluaran.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{item.nama_barang}</p>
                    <p className="text-xs text-muted-foreground">{item.tanggal}</p>
                  </div>
                  <p className="text-sm font-mono font-bold text-rose-600">
                    -{formatRupiah(item.harga_total)}
                  </p>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-4">Belum ada data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
